import React, { useState } from 'react';
import { formatMoney } from 'accounting';

import Button from 'components/shared/Button';
import Input from 'components/shared/Input';

import type { Dimension } from 'types/common_types';
import type { CustomSizeProductCustomOptions } from 'types/catalog_types';

export type CustomSizePickerProps = {
  customPriceLoading: boolean;
  isLoading: boolean;
  activeProductCustomOptions: CustomSizeProductCustomOptions;
  setCustomSize: (payload: Partial<Dimension>) => void;
  customHeight: number;
  customWidth: number;
  customPrice?: string;
  showPrice?: boolean;
  errorMessage?: string;
};

function CustomSizePicker({
  customPriceLoading,
  isLoading,
  activeProductCustomOptions,
  setCustomSize,
  customHeight,
  customWidth,
  customPrice = '0',
  showPrice = true,
  errorMessage,
}: CustomSizePickerProps) {
  const { shortmin, shortmax, longmin, longmax, increment } = activeProductCustomOptions;
  const [lockPerspective, setLockPerspective] = useState(false);

  function swapWidthHeight() {
    setCustomSize({
      width: customHeight,
      height: customWidth,
    });
  }

  function handleChangeWidth(width: number) {
    if (!lockPerspective) {
      setCustomSize({ width });
      return;
    }

    const diff = customWidth - width;
    const height = customHeight - diff;

    setCustomSize({ height, width });
  }

  function handleChangeHeight(height: number) {
    if (!lockPerspective) {
      setCustomSize({ height });
      return;
    }

    const diff = customHeight - height;
    const width = customWidth - diff;

    setCustomSize({ height, width });
  }

  return (
    <div className="custom_size_picker">
      <div className="size-picker-header">
        <div className="stats">
          <div>
            {shortmin}x{longmin} min
          </div>
          <div>-</div>
          <div>
            {shortmax}x{longmax} max
          </div>
        </div>

        {!showPrice && errorMessage && (
          <div className="custom-size-error">
            <span>{errorMessage}</span>
          </div>
        )}

        {showPrice && (
          <div className="custom-size-picker-price">
            {customPriceLoading && <span className="price">Calculating...</span>}
            <span className={`price ${customPriceLoading ? 'calculating' : ''}`}>
              {formatMoney(customPrice)}
            </span>
          </div>
        )}
      </div>
      <div className="custom_size_picker_inputs">
        <div className="size-picker-input">
          <Input
            id="input-width"
            label="Width"
            onChange={handleChangeWidth}
            value={customWidth}
            type="number"
            step={increment}
            disabled={isLoading}
          />
        </div>
        <span className="buttons">
          {lockPerspective && (
            <Button
              type="icon"
              icon="lock"
              className="lock"
              appearance="light"
              a11yText="Unlock Perspective"
              onClick={() => setLockPerspective(false)}
            />
          )}

          {!lockPerspective && (
            <Button
              type="icon"
              icon="unlock"
              appearance="light"
              a11yText="Lock Perspective"
              onClick={() => setLockPerspective(true)}
            />
          )}

          <Button
            type="icon"
            icon="exchange-alt"
            appearance="light"
            a11yText={lockPerspective ? 'Swap not available when locked' : 'Swap width & height'}
            disabled={lockPerspective}
            onClick={swapWidthHeight}
          />
        </span>
        <div className="size-picker-input">
          <Input
            id="input-height"
            label="Height"
            onChange={handleChangeHeight}
            value={customHeight}
            type="number"
            step={increment}
            disabled={isLoading}
          />
        </div>
      </div>
    </div>
  );
}

export default CustomSizePicker;
